import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  Timestamp,
  onSnapshot,
  QuerySnapshot,
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import {
  Employee,
  EmployeeStatus,
  Department,
  Onboarding,
  OnboardingStatus,
  TimeOffRequest,
  TimeOffStatus,
  TimeOffType,
  EmployeeDocument,
  PerformanceReview,
} from '@/models/hr.model';

const EMPLOYEES_COLLECTION = 'operationsEmployees';
const ONBOARDINGS_COLLECTION = 'onboardings';
const TIMEOFF_COLLECTION = 'timeOffRequests';
const EMPLOYEE_DOCUMENTS_COLLECTION = 'employeeDocuments';
const PERFORMANCE_REVIEWS_COLLECTION = 'performanceReviews';

export class HRService {
  // ========== Employee Management ==========
  
  static async getAllEmployees(): Promise<Employee[]> {
    const q = query(
      collection(db, EMPLOYEES_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEmployee(doc.id, doc.data()));
  }

  static async getEmployeeById(id: string): Promise<Employee | null> {
    const docRef = doc(db, EMPLOYEES_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) {
      return null;
    }
    return this.docToEmployee(docSnap.id, docSnap.data());
  }

  static subscribeToEmployees(
    callback: (employees: Employee[]) => void
  ): () => void {
    const q = query(
      collection(db, EMPLOYEES_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot: QuerySnapshot) => {
      const employees = snapshot.docs.map(doc =>
        this.docToEmployee(doc.id, doc.data())
      );
      callback(employees);
    });
  }

  static async filterEmployeesByDepartment(department: Department): Promise<Employee[]> {
    const q = query(
      collection(db, EMPLOYEES_COLLECTION),
      where('department', '==', department),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEmployee(doc.id, doc.data()));
  }

  static async filterEmployeesByStatus(status: EmployeeStatus): Promise<Employee[]> {
    const q = query(
      collection(db, EMPLOYEES_COLLECTION),
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEmployee(doc.id, doc.data()));
  }

  static async searchEmployees(searchTerm: string): Promise<Employee[]> {
    const allEmployees = await this.getAllEmployees();
    const lowerSearch = searchTerm.toLowerCase();
    
    return allEmployees.filter(employee =>
      employee.email.toLowerCase().includes(lowerSearch) ||
      employee.firstName.toLowerCase().includes(lowerSearch) ||
      employee.lastName.toLowerCase().includes(lowerSearch) ||
      employee.fullName.toLowerCase().includes(lowerSearch) ||
      employee.role.toLowerCase().includes(lowerSearch)
    );
  }

  static async createEmployee(employeeData: Partial<Employee>): Promise<string> {
    const now = Timestamp.now();
    const fullName = `${employeeData.firstName || ''} ${employeeData.lastName || ''}`.trim();
    
    const data: any = {
      email: employeeData.email || '',
      firstName: employeeData.firstName || '',
      lastName: employeeData.lastName || '',
      fullName,
      phoneNumber: employeeData.phoneNumber || null,
      department: employeeData.department || Department.operations,
      role: employeeData.role || '',
      managerId: employeeData.managerId || null,
      managerName: employeeData.managerName || null,
      status: employeeData.status || EmployeeStatus.pending,
      startDate: employeeData.startDate ? Timestamp.fromDate(employeeData.startDate) : now,
      endDate: employeeData.endDate ? Timestamp.fromDate(employeeData.endDate) : null,
      employeeNumber: employeeData.employeeNumber || null,
      photoUrl: employeeData.photoUrl || null,
      address: employeeData.address || null,
      city: employeeData.city || null,
      state: employeeData.state || null,
      zipCode: employeeData.zipCode || null,
      emergencyContactName: employeeData.emergencyContactName || null,
      emergencyContactPhone: employeeData.emergencyContactPhone || null,
      emergencyContactRelation: employeeData.emergencyContactRelation || null,
      salary: employeeData.salary || null,
      hourlyRate: employeeData.hourlyRate || null,
      employmentType: employeeData.employmentType || 'full_time',
      createdAt: now,
      lastUpdated: now,
      metadata: employeeData.metadata || {},
    };

    const docRef = await addDoc(collection(db, EMPLOYEES_COLLECTION), data);
    return docRef.id;
  }

  static async updateEmployee(
    id: string,
    updates: Partial<Employee>
  ): Promise<void> {
    const docRef = doc(db, EMPLOYEES_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.email !== undefined) updateData.email = updates.email;
    if (updates.firstName !== undefined) updateData.firstName = updates.firstName;
    if (updates.lastName !== undefined) updateData.lastName = updates.lastName;
    if (updates.firstName !== undefined || updates.lastName !== undefined) {
      updateData.fullName = `${updates.firstName || ''} ${updates.lastName || ''}`.trim();
    }
    if (updates.phoneNumber !== undefined) updateData.phoneNumber = updates.phoneNumber;
    if (updates.department !== undefined) updateData.department = updates.department;
    if (updates.role !== undefined) updateData.role = updates.role;
    if (updates.managerId !== undefined) updateData.managerId = updates.managerId;
    if (updates.managerName !== undefined) updateData.managerName = updates.managerName;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.startDate !== undefined) updateData.startDate = Timestamp.fromDate(updates.startDate);
    if (updates.endDate !== undefined) updateData.endDate = updates.endDate ? Timestamp.fromDate(updates.endDate) : null;
    if (updates.employeeNumber !== undefined) updateData.employeeNumber = updates.employeeNumber;
    if (updates.photoUrl !== undefined) updateData.photoUrl = updates.photoUrl;
    if (updates.address !== undefined) updateData.address = updates.address;
    if (updates.city !== undefined) updateData.city = updates.city;
    if (updates.state !== undefined) updateData.state = updates.state;
    if (updates.zipCode !== undefined) updateData.zipCode = updates.zipCode;
    if (updates.emergencyContactName !== undefined) updateData.emergencyContactName = updates.emergencyContactName;
    if (updates.emergencyContactPhone !== undefined) updateData.emergencyContactPhone = updates.emergencyContactPhone;
    if (updates.emergencyContactRelation !== undefined) updateData.emergencyContactRelation = updates.emergencyContactRelation;
    if (updates.salary !== undefined) updateData.salary = updates.salary;
    if (updates.hourlyRate !== undefined) updateData.hourlyRate = updates.hourlyRate;
    if (updates.employmentType !== undefined) updateData.employmentType = updates.employmentType;

    await updateDoc(docRef, updateData);
  }

  // ========== Onboarding Management ==========

  static async getOnboardingByEmployeeId(employeeId: string): Promise<Onboarding | null> {
    const q = query(
      collection(db, ONBOARDINGS_COLLECTION),
      where('employeeId', '==', employeeId)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }
    return this.docToOnboarding(snapshot.docs[0].id, snapshot.docs[0].data());
  }

  static async getAllOnboardings(): Promise<Onboarding[]> {
    const q = query(
      collection(db, ONBOARDINGS_COLLECTION),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToOnboarding(doc.id, doc.data()));
  }

  static async createOnboarding(onboardingData: Partial<Onboarding>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: onboardingData.employeeId || '',
      employeeName: onboardingData.employeeName || '',
      status: onboardingData.status || OnboardingStatus.notStarted,
      startDate: onboardingData.startDate ? Timestamp.fromDate(onboardingData.startDate) : now,
      completedDate: onboardingData.completedDate ? Timestamp.fromDate(onboardingData.completedDate) : null,
      tasks: onboardingData.tasks || [],
      notes: onboardingData.notes || null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, ONBOARDINGS_COLLECTION), data);
    return docRef.id;
  }

  static async updateOnboarding(
    id: string,
    updates: Partial<Onboarding>
  ): Promise<void> {
    const docRef = doc(db, ONBOARDINGS_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.completedDate !== undefined) updateData.completedDate = updates.completedDate ? Timestamp.fromDate(updates.completedDate) : null;
    if (updates.tasks !== undefined) updateData.tasks = updates.tasks;
    if (updates.notes !== undefined) updateData.notes = updates.notes;

    await updateDoc(docRef, updateData);
  }

  // ========== Time Off Management ==========

  static async getAllTimeOffRequests(): Promise<TimeOffRequest[]> {
    const q = query(
      collection(db, TIMEOFF_COLLECTION),
      orderBy('requestedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTimeOffRequest(doc.id, doc.data()));
  }

  static async getTimeOffRequestsByEmployee(employeeId: string): Promise<TimeOffRequest[]> {
    const q = query(
      collection(db, TIMEOFF_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('requestedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToTimeOffRequest(doc.id, doc.data()));
  }

  static async createTimeOffRequest(requestData: Partial<TimeOffRequest>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: requestData.employeeId || '',
      employeeName: requestData.employeeName || '',
      type: requestData.type || TimeOffType.vacation,
      startDate: requestData.startDate ? Timestamp.fromDate(requestData.startDate) : now,
      endDate: requestData.endDate ? Timestamp.fromDate(requestData.endDate) : now,
      days: requestData.days || 0,
      status: requestData.status || TimeOffStatus.pending,
      reason: requestData.reason || null,
      requestedAt: now,
      approvedBy: requestData.approvedBy || null,
      approvedAt: requestData.approvedAt ? Timestamp.fromDate(requestData.approvedAt) : null,
      rejectedBy: requestData.rejectedBy || null,
      rejectedAt: requestData.rejectedAt ? Timestamp.fromDate(requestData.rejectedAt) : null,
      rejectionReason: requestData.rejectionReason || null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, TIMEOFF_COLLECTION), data);
    return docRef.id;
  }

  static async updateTimeOffRequest(
    id: string,
    updates: Partial<TimeOffRequest>
  ): Promise<void> {
    const docRef = doc(db, TIMEOFF_COLLECTION, id);
    const updateData: any = {
      lastUpdated: Timestamp.now(),
    };

    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.approvedBy !== undefined) updateData.approvedBy = updates.approvedBy;
    if (updates.approvedAt !== undefined) updateData.approvedAt = updates.approvedAt ? Timestamp.fromDate(updates.approvedAt) : null;
    if (updates.rejectedBy !== undefined) updateData.rejectedBy = updates.rejectedBy;
    if (updates.rejectedAt !== undefined) updateData.rejectedAt = updates.rejectedAt ? Timestamp.fromDate(updates.rejectedAt) : null;
    if (updates.rejectionReason !== undefined) updateData.rejectionReason = updates.rejectionReason;

    await updateDoc(docRef, updateData);
  }

  // ========== Document Management ==========

  static async getEmployeeDocuments(employeeId: string): Promise<EmployeeDocument[]> {
    const q = query(
      collection(db, EMPLOYEE_DOCUMENTS_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('uploadedAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToEmployeeDocument(doc.id, doc.data()));
  }

  static async createEmployeeDocument(documentData: Partial<EmployeeDocument>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: documentData.employeeId || '',
      name: documentData.name || '',
      type: documentData.type || 'other',
      fileUrl: documentData.fileUrl || '',
      expiresAt: documentData.expiresAt ? Timestamp.fromDate(documentData.expiresAt) : null,
      uploadedAt: now,
      uploadedBy: documentData.uploadedBy || '',
      metadata: documentData.metadata || {},
    };

    const docRef = await addDoc(collection(db, EMPLOYEE_DOCUMENTS_COLLECTION), data);
    return docRef.id;
  }

  // ========== Performance Reviews ==========

  static async getPerformanceReviewsByEmployee(employeeId: string): Promise<PerformanceReview[]> {
    const q = query(
      collection(db, PERFORMANCE_REVIEWS_COLLECTION),
      where('employeeId', '==', employeeId),
      orderBy('reviewDate', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => this.docToPerformanceReview(doc.id, doc.data()));
  }

  static async createPerformanceReview(reviewData: Partial<PerformanceReview>): Promise<string> {
    const now = Timestamp.now();
    const data: any = {
      employeeId: reviewData.employeeId || '',
      employeeName: reviewData.employeeName || '',
      reviewPeriod: reviewData.reviewPeriod || '',
      reviewDate: reviewData.reviewDate ? Timestamp.fromDate(reviewData.reviewDate) : now,
      reviewedBy: reviewData.reviewedBy || '',
      reviewedByName: reviewData.reviewedByName || '',
      rating: reviewData.rating || null,
      strengths: reviewData.strengths || [],
      areasForImprovement: reviewData.areasForImprovement || [],
      goals: reviewData.goals || [],
      notes: reviewData.notes || null,
      createdAt: now,
      lastUpdated: now,
    };

    const docRef = await addDoc(collection(db, PERFORMANCE_REVIEWS_COLLECTION), data);
    return docRef.id;
  }

  // ========== Helper Methods ==========

  private static docToEmployee(id: string, data: any): Employee {
    return {
      id,
      email: data.email || '',
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      fullName: data.fullName || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
      phoneNumber: data.phoneNumber,
      department: data.department || Department.operations,
      role: data.role || '',
      managerId: data.managerId,
      managerName: data.managerName,
      status: data.status || EmployeeStatus.pending,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate(),
      employeeNumber: data.employeeNumber,
      photoUrl: data.photoUrl,
      address: data.address,
      city: data.city,
      state: data.state,
      zipCode: data.zipCode,
      emergencyContactName: data.emergencyContactName,
      emergencyContactPhone: data.emergencyContactPhone,
      emergencyContactRelation: data.emergencyContactRelation,
      salary: data.salary,
      hourlyRate: data.hourlyRate,
      employmentType: data.employmentType || 'full_time',
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
      metadata: data.metadata || {},
    };
  }

  private static docToOnboarding(id: string, data: any): Onboarding {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      status: data.status || OnboardingStatus.notStarted,
      startDate: data.startDate?.toDate() || new Date(),
      completedDate: data.completedDate?.toDate(),
      tasks: (data.tasks || []).map((task: any) => ({
        id: task.id || '',
        employeeId: task.employeeId || '',
        title: task.title || '',
        description: task.description,
        assignedTo: task.assignedTo,
        dueDate: task.dueDate?.toDate(),
        completed: task.completed || false,
        completedAt: task.completedAt?.toDate(),
        completedBy: task.completedBy,
        category: task.category || 'other',
        createdAt: task.createdAt?.toDate() || new Date(),
      })),
      notes: data.notes,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToTimeOffRequest(id: string, data: any): TimeOffRequest {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      type: data.type || TimeOffType.vacation,
      startDate: data.startDate?.toDate() || new Date(),
      endDate: data.endDate?.toDate() || new Date(),
      days: data.days || 0,
      status: data.status || TimeOffStatus.pending,
      reason: data.reason,
      requestedAt: data.requestedAt?.toDate() || new Date(),
      approvedBy: data.approvedBy,
      approvedAt: data.approvedAt?.toDate(),
      rejectedBy: data.rejectedBy,
      rejectedAt: data.rejectedAt?.toDate(),
      rejectionReason: data.rejectionReason,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }

  private static docToEmployeeDocument(id: string, data: any): EmployeeDocument {
    return {
      id,
      employeeId: data.employeeId || '',
      name: data.name || '',
      type: data.type || 'other',
      fileUrl: data.fileUrl || '',
      expiresAt: data.expiresAt?.toDate(),
      uploadedAt: data.uploadedAt?.toDate() || new Date(),
      uploadedBy: data.uploadedBy || '',
      metadata: data.metadata || {},
    };
  }

  private static docToPerformanceReview(id: string, data: any): PerformanceReview {
    return {
      id,
      employeeId: data.employeeId || '',
      employeeName: data.employeeName || '',
      reviewPeriod: data.reviewPeriod || '',
      reviewDate: data.reviewDate?.toDate() || new Date(),
      reviewedBy: data.reviewedBy || '',
      reviewedByName: data.reviewedByName || '',
      rating: data.rating,
      strengths: data.strengths || [],
      areasForImprovement: data.areasForImprovement || [],
      goals: data.goals || [],
      notes: data.notes,
      createdAt: data.createdAt?.toDate() || new Date(),
      lastUpdated: data.lastUpdated?.toDate() || new Date(),
    };
  }
}

